/**
 * Forecast page model — pure functions: delivery horizon per line (late / next 30 d / next 90 d),
 * grouping by LPO, the risk KPIs and the catalog data gaps behind the numbers.
 */
import { ageDays, toNumber } from '@/lib/format';

const num = (value) => toNumber(value) ?? 0;
const text = (value) => String(value ?? '').trim();
const lower = (value) => text(value).toLowerCase();

/** Each expected-delivery line with its horizon: days > 0 = past the expected date. */
export function normalizeLines(lines, { now } = {}) {
  return (Array.isArray(lines) ? lines : []).map((line, index) => {
    const expected = text(line.expected_date);
    const days = expected ? ageDays(expected, { now }) : null;
    const remaining = num(line.remaining_qty);
    const unitPrice = num(line.unit_price);
    let horizon = 'nodate';
    if (days !== null && days > 0) horizon = 'late';
    else if (days !== null && days >= -30) horizon = 'next30';
    else if (days !== null && days >= -90) horizon = 'next90';
    else if (days !== null) horizon = 'later';
    return {
      ...line,
      key: `${line.lpo_id || line.lpo_number}-${line.part_number}-${index}`,
      lpoKey: text(line.lpo_id) || text(line.lpo_number) || 'no-lpo',
      expected,
      days,
      daysLate: days !== null && days > 0 ? days : 0,
      horizon,
      ordered: num(line.ordered_qty),
      delivered: num(line.delivered_qty),
      remaining,
      unitPrice,
      // /forecast may leave prices out for callers without Procurement access: then there is no AED value.
      hasPrice: line.unit_price !== undefined && line.unit_price !== null,
      remainingValue: remaining * unitPrice,
      supplierKey: text(line.supplier) || '__none__',
    };
  });
}

export const HORIZONS = [
  { key: 'late', label: 'Late' },
  { key: 'next30', label: 'Next 30 d' },
  { key: 'next90', label: 'Next 90 d' },
  { key: 'all', label: 'All open' },
];

export function inHorizon(line, horizon) {
  if (horizon === 'late') return line.horizon === 'late';
  if (horizon === 'next30') return line.horizon === 'next30';
  if (horizon === 'next90') return line.horizon === 'next30' || line.horizon === 'next90';
  return true;
}

export function horizonCounts(lines) {
  return Object.fromEntries(HORIZONS.map(({ key }) => [key, lines.filter((line) => inHorizon(line, key)).length]));
}

/** Late first (most late on top), then soonest expected, lines without a date last. */
export function compareLines(a, b) {
  if (a.daysLate !== b.daysLate) return b.daysLate - a.daysLate;
  if (a.expected !== b.expected) {
    if (!a.expected) return 1;
    if (!b.expected) return -1;
    return a.expected < b.expected ? -1 : 1;
  }
  return text(a.lpo_number).localeCompare(text(b.lpo_number), undefined, { numeric: true })
    || text(a.part_number).localeCompare(text(b.part_number));
}

export function filterLines(lines, { horizon = 'all', search = '', supplier = null, partNumbers = null } = {}) {
  const needle = lower(search);
  return lines.filter((line) => {
    if (!inHorizon(line, horizon)) return false;
    if (supplier && line.supplierKey !== supplier) return false;
    if (partNumbers && !partNumbers.has(line.part_number)) return false;
    if (!needle) return true;
    return [line.part_number, line.description, line.lpo_number].some((value) => lower(value).includes(needle));
  }).sort(compareLines);
}

/**
 * Parts to order inside the page's scope. Search matches part / description; the data-gaps toggle keeps
 * its parts; a supplier keeps the parts that supplier delivers on the open LPOs in scope (`supplierParts`)
 * or is the catalog's preferred supplier for.
 */
export function filterSuggestions(suggestions, { search = '', partNumbers = null, supplier = null, supplierLabel = '', supplierParts = null } = {}) {
  const needle = lower(search);
  return (Array.isArray(suggestions) ? suggestions : []).filter((s) => {
    if (partNumbers && !partNumbers.has(s.part_number)) return false;
    if (supplier) {
      const onSupplierLpo = Boolean(supplierParts && supplierParts.has(s.part_number));
      const preferred = supplier !== '__none__' && text(s.preferred_supplier) && lower(s.preferred_supplier) === lower(supplierLabel);
      if (!onSupplierLpo && !preferred) return false;
    }
    if (needle && ![s.part_number, s.description].some((value) => lower(value).includes(needle))) return false;
    return true;
  });
}

/** Remaining pieces per expected month ('YYYY-MM', or 'Unscheduled' without a usable date), oldest first. */
export function monthlyRemaining(lines) {
  const months = new Map();
  lines.forEach((line) => {
    const match = /^(\d{4})-(\d{2})/.exec(line.expected || '');
    const key = match ? `${match[1]}-${match[2]}` : 'Unscheduled';
    months.set(key, (months.get(key) || 0) + line.remaining);
  });
  return [...months.entries()].map(([key, totalQty]) => ({ key, totalQty })).sort((a, b) => a.key.localeCompare(b.key));
}

/** Lines grouped by LPO: totals, the LPO's expected date and lateness. Most late LPO first. */
export function groupByLpo(lines) {
  const groups = new Map();
  lines.forEach((line) => {
    const group = groups.get(line.lpoKey) || {
      key: line.lpoKey,
      lpoNumber: text(line.lpo_number),
      supplier: text(line.supplier),
      projectName: text(line.project_name),
      lpoId: text(line.lpo_id),
      // 23 #9: when the supplier was last chased and whether a new date was recorded since
      chase: {
        last_chase_at: text(line.last_chase_at),
        chase_count: num(line.chase_count),
        eta_revised_at: text(line.eta_revised_at),
        original_expected_date: text(line.original_expected_date),
        expected: line.expected,
      },
      expected: line.expected,
      daysLate: 0,
      lines: [],
      ordered: 0,
      delivered: 0,
      remaining: 0,
    };
    group.lines.push(line);
    group.ordered += line.ordered;
    group.delivered += Math.min(line.delivered, line.ordered);
    group.remaining += line.remaining;
    group.daysLate = Math.max(group.daysLate, line.daysLate);
    if (line.expected && (!group.expected || line.expected < group.expected)) group.expected = line.expected;
    groups.set(line.lpoKey, group);
  });
  return [...groups.values()].sort((a, b) => (b.daysLate - a.daysLate)
    || (a.expected || '9999').localeCompare(b.expected || '9999')
    || a.lpoNumber.localeCompare(b.lpoNumber, undefined, { numeric: true }));
}

export function lineTotals(lines) {
  return {
    lines: lines.length,
    lpos: new Set(lines.map((line) => line.lpoKey)).size,
    ordered: lines.reduce((sum, line) => sum + line.ordered, 0),
    delivered: lines.reduce((sum, line) => sum + Math.min(line.delivered, line.ordered), 0),
    remaining: lines.reduce((sum, line) => sum + line.remaining, 0),
  };
}

/**
 * Risk KPIs: late lines (and LPOs, oldest), lines due in the next 30 days, parts to order (short pcs,
 * earliest order-by date, how many are stock gaps), and the value still to be delivered (remaining × LPO unit price).
 */
export function forecastKpis(lines, suggestions) {
  const late = lines.filter((line) => line.horizon === 'late');
  const next30 = lines.filter((line) => line.horizon === 'next30');
  const orderDates = suggestions.map((s) => text(s.recommended_order_date)).filter(Boolean).sort();
  const unpriced = lines.filter((line) => line.remaining > 0 && !line.unitPrice).length;
  return {
    lateLines: late.length,
    lateLpos: new Set(late.map((line) => line.lpoKey)).size,
    oldestLateDays: late.reduce((max, line) => Math.max(max, line.daysLate), 0),
    next30Lines: next30.length,
    next30Pcs: next30.reduce((sum, line) => sum + line.remaining, 0),
    next30Lpos: new Set(next30.map((line) => line.lpoKey)).size,
    partsToOrder: suggestions.length,
    shortPcs: suggestions.reduce((sum, s) => sum + num(s.shortage_qty), 0),
    // Parts whose whole shortage is delivered pieces that are not in stock (check stock before ordering).
    stockGapParts: suggestions.filter((s) => shortageBreakdown(s).coveredByGap).length,
    orderBy: orderDates[0] || '',
    stillToDeliverValue: lines.reduce((sum, line) => sum + line.remainingValue, 0),
    pricesShown: lines.length === 0 || lines.some((line) => line.hasPrice),
    stillToDeliverPcs: lines.reduce((sum, line) => sum + line.remaining, 0),
    openLpos: new Set(lines.map((line) => line.lpoKey)).size,
    unpricedLines: unpriced,
  };
}

/**
 * Parts on this page whose catalog data the forecast relies on is missing: not in the catalog at all,
 * or no category / lead time (the 14-day default is then used) / preferred supplier.
 * → [{ partNumber, description, issues: ['Not in catalog'] | ['No lead time', 'No supplier', …] }]
 */
export function dataGaps(lines, suggestions) {
  const parts = new Map();
  const touch = (partNumber, description) => {
    const key = text(partNumber);
    if (!key) return null;
    if (!parts.has(key)) parts.set(key, { partNumber: key, description: text(description), inCatalog: true, category: '', leadTimeSet: true, supplier: '' });
    return parts.get(key);
  };
  lines.forEach((line) => {
    const part = touch(line.part_number, line.description);
    if (!part) return;
    if (line.in_catalog === false) part.inCatalog = false;
    if (line.category) part.category = line.category;
    if (line.in_catalog !== undefined && !(num(line.lead_time_days) > 0)) part.leadTimeSet = false;
    if (line.preferred_supplier) part.supplier = line.preferred_supplier;
  });
  suggestions.forEach((s) => {
    const part = touch(s.part_number, s.description);
    if (!part) return;
    if (s.in_catalog === false) part.inCatalog = false;
    if (s.category) part.category = s.category;
    if (s.lead_time_is_default) part.leadTimeSet = false;
    if (s.preferred_supplier) part.supplier = s.preferred_supplier;
  });
  return [...parts.values()]
    .map((part) => {
      const issues = [];
      if (!part.inCatalog) issues.push('Not in catalog');
      else {
        if (!part.category) issues.push('No category');
        if (!part.leadTimeSet) issues.push('No lead time');
        if (!part.supplier) issues.push('No supplier');
      }
      return { partNumber: part.partNumber, description: part.description, issues };
    })
    .filter((part) => part.issues.length)
    .sort((a, b) => a.partNumber.localeCompare(b.partNumber));
}

export const FORECAST_EXPORT_COLUMNS = [
  { key: 'lpo_number', label: 'LPO' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'project_name', label: 'Project' },
  { key: 'part_number', label: 'Part number' },
  { key: 'description', label: 'Description' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'remaining', label: 'Remaining' },
  { key: 'unitPrice', label: 'Unit price (AED)' },
  { key: 'remainingValue', label: 'Remaining value (AED)' },
  { key: 'expected', label: 'Expected date' },
  { key: 'daysLate', label: 'Days late', value: (line) => line.daysLate || '' },
];

/* ------------------------------------------------------------------ wave 2: delivery timeline */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TIMELINE_MAX_MONTHS = 15;
const pad2 = (value) => String(value).padStart(2, '0');
/** 'YYYY-MM-DD' (the expected dates are calendar days) → { y, m (0-11), d }. */
const dayParts = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return match ? { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) } : null;
};
const daysInMonth = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const monthIndex = (parts) => parts.y * 12 + parts.m;

/**
 * The "Delivery timeline" (23 #3): one lane per LPO on a month axis from the oldest expected month (or
 * `TIMELINE_MAX_MONTHS` back at most) to three months after today, a Today marker and the late zone before it.
 * Counts LPOs and lines — never pieces of different parts added together.
 *  today: 'YYYY-MM-DD' (Dubai calendar day).
 * → { months: [{ key, label, year }], todayPct, lanes: [{ key, lpoNumber, supplier, expected, pct, clipped,
 *     late, daysLate, daysUntil, remaining, lines }], hidden, undated, nextWindowEmpty }
 */
export function buildTimeline(lines, { today, maxLanes = 6 } = {}) {
  const todayParts = dayParts(today);
  if (!todayParts) return null;
  const groups = groupByLpo(lines.filter((line) => line.remaining > 0));
  const dated = groups.filter((group) => dayParts(group.expected));
  const undated = groups.length - dated.length;
  const todayIndex = monthIndex(todayParts);
  const earliest = dated.reduce((min, group) => Math.min(min, monthIndex(dayParts(group.expected))), todayIndex);
  const latest = dated.reduce((max, group) => Math.max(max, monthIndex(dayParts(group.expected))), todayIndex + 3);
  const end = Math.min(Math.max(latest, todayIndex + 3), todayIndex + TIMELINE_MAX_MONTHS);
  const start = Math.max(earliest, end - TIMELINE_MAX_MONTHS + 1);
  const count = end - start + 1;
  const months = Array.from({ length: count }, (_, offset) => {
    const index = start + offset;
    const y = Math.floor(index / 12);
    const m = index % 12;
    return { key: `${y}-${pad2(m + 1)}`, label: MONTH_NAMES[m], year: y };
  });
  const pctOf = (parts) => {
    const index = monthIndex(parts) - start;
    return ((index + (parts.d - 1) / daysInMonth(parts.y, parts.m)) / count) * 100;
  };
  const todayPct = pctOf(todayParts);
  const lanes = dated.map((group) => {
    const parts = dayParts(group.expected);
    const raw = pctOf(parts);
    const days = lines.find((line) => line.lpoKey === group.key && line.expected === group.expected)?.days ?? null;
    return {
      key: group.key,
      lpoNumber: group.lpoNumber,
      supplier: group.supplier,
      expected: group.expected,
      pct: Math.max(0, Math.min(100, raw)),
      clipped: raw < 0 ? 'start' : raw > 100 ? 'end' : null,
      late: group.daysLate > 0,
      daysLate: group.daysLate,
      daysUntil: days !== null && days < 0 ? -days : 0,
      remaining: group.remaining,
      delivered: group.delivered,
      lines: group.lines.length,
    };
  });
  const nextWindowEmpty = !lanes.some((lane) => !lane.late && lane.daysUntil <= 90);
  return { months, todayPct, lanes: lanes.slice(0, maxLanes), hidden: Math.max(0, lanes.length - maxLanes), undated, nextWindowEmpty };
}

/**
 * Pieces of a part delivered direct to site on the open LPOs behind a suggestion, from
 * GET /lpos/deliveries-by-destination ({ [lpoId]: { to_site: { [part]: qty } } }). undefined when unknown.
 */
export function siteDeliveredFor(suggestion, byLpo) {
  if (!byLpo || typeof byLpo !== 'object') return undefined;
  const lines = Array.isArray(suggestion?.demand_lines) ? suggestion.demand_lines : [];
  if (!lines.length) return undefined;
  const part = text(suggestion?.part_number);
  let known = false;
  const total = lines.reduce((sum, line) => {
    const entry = byLpo[text(line.lpo_id)];
    if (!entry) return sum;
    known = true;
    return sum + num(entry.to_site?.[part]);
  }, 0);
  return known ? total : undefined;
}

/** Suggestions with `delivered_to_site_on_lpos` filled in from the LPO deliveries (when /forecast does not send it). */
export function withSiteDeliveries(suggestions, byLpo) {
  const list = Array.isArray(suggestions) ? suggestions : [];
  if (!byLpo) return list;
  return list.map((s) => {
    if (s.delivered_to_site_on_lpos !== undefined && s.delivered_to_site_on_lpos !== null) return s;
    const toSite = siteDeliveredFor(s, byLpo);
    return toSite === undefined ? s : { ...s, delivered_to_site_on_lpos: toSite };
  });
}

/**
 * The "Why short?" sums for one order suggestion (23 #5): demand (open LPO lines + reservations)
 * − available (stock on hand − reserved) − still incoming = short.
 *
 * It also says how much of the shortage is pieces that were DELIVERED on those LPOs but are not in stock now
 * (`gapQty`): Forecast counts every open LPO line as demand and only stock + what is still to come as supply,
 * so a piece that arrived but is not in stock (another store, issued, or delivered direct to site) shows up as
 * "short" — buying it again may buy the same pieces twice. `buyQty` is what is left once that is taken out.
 *  - stockGap / missingFromStock: pieces received INTO stock that are not there now → check Inventory locations.
 *  - deliveredToSite: pieces that went direct to site (never in stock) — known when the LPO deliveries were
 *    loaded (`delivered_to_site_on_lpos`); `toSiteKnown` false = it may be part of the gap.
 */
export function shortageBreakdown(suggestion) {
  const s = suggestion || {};
  const demandLines = Array.isArray(s.demand_lines) ? s.demand_lines : [];
  const stockOnHand = num(s.stock_on_hand ?? s.current_available);
  const reserved = num(s.reserved_qty);
  const delivered = num(s.delivered_on_lpos);
  const short = num(s.shortage_qty);
  const toSiteKnown = s.delivered_to_site_on_lpos !== undefined && s.delivered_to_site_on_lpos !== null;
  const deliveredToSite = toSiteKnown ? Math.min(delivered, Math.max(0, num(s.delivered_to_site_on_lpos))) : 0;
  const deliveredToStock = delivered - deliveredToSite;
  const notInStock = Math.max(0, delivered - stockOnHand);
  const gapQty = Math.min(short, notInStock);
  const missingFromStock = Math.max(0, deliveredToStock - stockOnHand);
  return {
    demand: num(s.demand_qty ?? (demandLines.reduce((sum, line) => sum + num(line.ordered_qty), 0) + reserved)),
    lpoDemand: num(s.lpo_demand_qty ?? demandLines.reduce((sum, line) => sum + num(line.ordered_qty), 0)),
    reserved,
    stockOnHand,
    available: num(s.current_available),
    incoming: num(s.incoming_qty),
    short,
    demandLines,
    locations: Array.isArray(s.stock_by_location) ? s.stock_by_location : [],
    delivered,
    deliveredToSite,
    deliveredToStock,
    toSiteKnown,
    gapQty,
    buyQty: Math.max(0, short - gapQty),
    // The whole shortage is delivered pieces that are not in stock: check stock, do not just order again.
    coveredByGap: short > 0 && gapQty >= short,
    missingFromStock,
    // Received into stock but not there now: the shortage may be a stock-location / part-number problem.
    stockGap: missingFromStock > 0,
    hasBreakdown: s.demand_qty !== undefined,
  };
}

/** Supplier to order a short part from: the catalog's preferred one, else the supplier of its last LPO. */
export function suggestedSupplier(suggestion) {
  const preferred = text(suggestion?.preferred_supplier);
  if (preferred) return { name: preferred, source: 'catalog' };
  const last = text(suggestion?.last_lpo_supplier);
  if (last) return { name: last, source: 'last_lpo', lpoNumber: text(suggestion?.last_lpo_number) };
  return { name: '', source: null };
}

/**
 * New-LPO prefill for some short parts: one line per part, the shared supplier if there is one.
 * The quantity is what is left to buy (`buyQty`). When delivered-but-not-in-stock pieces explain part of the
 * shortage the line says so (`forecast`), and when they explain all of it the quantity is left EMPTY — the
 * buyer has to decide (the panel offers "Use 20"), so nothing is re-ordered without saying why.
 */
export function lpoPrefillFor(suggestions) {
  const list = Array.isArray(suggestions) ? suggestions : [];
  const suppliers = [...new Set(list.map((s) => suggestedSupplier(s).name).filter(Boolean))];
  const lines = list.map((s) => {
    const b = shortageBreakdown(s);
    const gapLpo = b.demandLines.find((line) => num(line.delivered_qty) > 0);
    const line = {
      part_number: s.part_number,
      description: s.description || '',
      ordered_qty: b.buyQty > 0 ? b.buyQty : '',
      unit_price: s.last_unit_price ? num(s.last_unit_price) : '',
    };
    if (b.gapQty > 0) {
      line.forecast = {
        short: b.short,
        gapQty: b.gapQty,
        buyQty: b.buyQty,
        delivered: b.delivered,
        deliveredToSite: b.deliveredToSite,
        stockOnHand: b.stockOnHand,
        lpoNumber: gapLpo ? text(gapLpo.lpo_number) : '',
      };
    }
    return line;
  });
  return {
    supplier: suppliers.length === 1 ? suppliers[0] : '',
    lines,
    origin: 'forecast',
    stockGapParts: lines.filter((line) => line.forecast).length,
    source: `From Forecast · ${list.length === 1 ? `${list[0].part_number} is ${formatShort(list[0].shortage_qty)} short` : `${list.length} short parts`}`,
  };
}

const formatShort = (value) => String(num(value));
